import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState, type JSX, type KeyboardEvent } from "react";
import { Input, Label } from "@mailflare/ui";
import type { AddressObject } from "@mailflare/shared";

import { parseRecipient, searchContacts } from "./contacts-api";

export interface RecipientsFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: readonly AddressObject[];
  readonly onChange: (next: AddressObject[]) => void;
  readonly placeholder?: string;
}

const SUGGESTION_PREFIX = "recipient-suggestion";

function sameAddress(a: AddressObject, b: AddressObject): boolean {
  return a.address.toLowerCase() === b.address.toLowerCase();
}

/**
 * Address input with chips and best-effort autocomplete. The contacts endpoint
 * is optional; when absent suggestions simply stay empty.
 */
export function RecipientsField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: RecipientsFieldProps): JSX.Element {
  const [text, setText] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const suggestionsQuery = useQuery({
    queryKey: ["contacts", text],
    queryFn: ({ signal }) => searchContacts(text, signal),
    enabled: text.trim().length >= 2,
    staleTime: 60_000,
  });
  const suggestions = suggestionsQuery.data ?? [];

  const add = (input: string): void => {
    const parsed = parseRecipient(input);
    if (parsed === null) return;
    if (value.some((existing) => sameAddress(existing, parsed))) {
      setText("");
      return;
    }
    onChange([...value, parsed]);
    setText("");
    setActiveIndex(-1);
  };

  const removeAt = (index: number): void => {
    onChange(value.filter((_, current) => current !== index));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (
      (event.key === "Enter" || event.key === "," || event.key === "Tab") &&
      text.trim().length > 0
    ) {
      event.preventDefault();
      add(text);
      return;
    }
    if (event.key === "Backspace" && text.length === 0 && value.length > 0) {
      removeAt(value.length - 1);
      return;
    }
    if (event.key === "ArrowDown" && suggestions.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, suggestions.length - 1));
      return;
    }
    if (event.key === "ArrowUp" && suggestions.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
      return;
    }
    if (event.key === "Escape") {
      setActiveIndex(-1);
    }
  };

  const listOpen = suggestionsQuery.isSuccess && suggestions.length > 0 && text.trim().length >= 2;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={`${label} recipients`}>
          {value.map((address, index) => (
            <li
              key={`${address.address}-${index}`}
              className="bg-muted flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
            >
              <span>{address.name ?? address.address}</span>
              <button
                type="button"
                aria-label={`Remove ${address.address}`}
                onClick={() => removeAt(index)}
                className="focus-visible:ring-ring rounded-full focus-visible:ring-2 focus-visible:outline-none"
              >
                <X aria-hidden className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="relative">
        <Input
          id={id}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (text.trim().length > 0) add(text);
          }}
          placeholder={placeholder ?? "name@example.com"}
          autoComplete="off"
          role="combobox"
          aria-expanded={listOpen}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          {...(listOpen && activeIndex >= 0
            ? { "aria-activedescendant": `${SUGGESTION_PREFIX}-${activeIndex}` }
            : {})}
        />
        {listOpen ? (
          <ul
            id={`${id}-listbox`}
            role="listbox"
            aria-label={`${label} suggestions`}
            className="bg-popover text-popover-foreground absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border p-1 shadow-md"
          >
            {suggestions.map((suggestion, index) => (
              <li key={suggestion.address}>
                <button
                  type="button"
                  id={`${SUGGESTION_PREFIX}-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className="hover:bg-accent w-full rounded-sm px-2 py-1.5 text-left text-sm"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    add(suggestion.address);
                  }}
                >
                  {suggestion.name !== undefined ? `${suggestion.name} ` : ""}
                  <span className="text-muted-foreground">{suggestion.address}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
